import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { hasPermissions } from '../../../auth/hasPermissions';
import { API_GET, API_POST, useConfig } from '../../../api/api';
import { request } from '../../ai-chat/aiChatApi';
import { tokens } from '../../../global/theme/tokens';
import ExoAlert from '../../exo/ExoAlert';
import { htmlToQuillDelta } from '../../exo/rich-text-editor/quillHtmlToDelta.ts';

const STYLES = [
  { key: 'formal', label: 'Formal' },
  { key: 'casual', label: 'Casual' },
  { key: 'concise', label: 'Shorter and clearer' },
  { key: 'spelling', label: 'Spelling and grammar only' },
];

/**
 * The "improve with AI" quick action of the rich text editor: rewrites the
 * selected text (or the whole text when nothing is selected) in a chosen
 * style through POST ai/rewrite-text, which runs on the same AI provider
 * and budget as the AI chat. Hidden for users without the AI chat
 * permission, disabled with a hint when the AI budget is used up, and
 * glowing while text is selected. Picking a style closes the menu at once;
 * the toolbar button shows a spinner while the AI works (the request is
 * synchronous and can take a few seconds) and an error opens the app's
 * alert dialog.
 *
 * getEditor returns the Quill instance, getRange the selection to work on
 * (the last known one, since opening the menu blurs the editor).
 */
export const AiRewriteStyle = ({ getEditor, getRange, hasSelection }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const allowed = hasPermissions('ai-chat', 'read');

  const [anchorEl, setAnchorEl] = useState(null);
  const [customAnchorEl, setCustomAnchorEl] = useState(null);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState({ type: 'error', open: false });
  // undefined = unknown, null = the instance has no AI budget, else { available, usedPercent, ... }
  const [budget, setBudget] = useState(undefined);

  useEffect(() => {
    if (!config || !allowed) return;
    request(config, 'ai/budget', API_GET)
      .then((res) => setBudget(res.data ?? null))
      .catch(() => setBudget(undefined));
  }, [config, allowed]);

  if (!allowed) return null;

  const exhausted = Boolean(budget?.available) && budget.usedPercent >= 100;
  const disabled = busy || exhausted || !config;

  async function rewrite(style, customInstruction = '') {
    const editor = getEditor();
    if (!editor || busy) return;

    const range = getRange();
    const partial = Boolean(range && range.length > 0);
    const text = partial
      ? editor.getText(range.index, range.length)
      : editor.root.innerHTML;

    // Close the menus right away: the spinner on the toolbar button is the
    // feedback while the request runs.
    setAnchorEl(null);
    setCustomAnchorEl(null);

    if (!text || !text.trim()) return;

    setBusy(true);
    try {
      const res = await request(
        config,
        'ai/rewrite-text',
        API_POST({
          text,
          style,
          custom_instruction: style === 'custom' ? customInstruction : null,
        }),
      );
      const rewritten = res.data?.text ?? '';

      if (rewritten) {
        if (partial) {
          const format = editor.getFormat(range);
          editor.deleteText(range.index, range.length, 'user');
          editor.insertText(range.index, rewritten, format, 'user');
          editor.setSelection(range.index, rewritten.length, 'silent');
        } else {
          editor.setContents(htmlToQuillDelta(rewritten), 'user');
        }
      }

      if (typeof res.data?.usedPercent === 'number') {
        setBudget((current) =>
          current ? { ...current, usedPercent: res.data.usedPercent } : current,
        );
      }
      setCustom('');
    } catch (err) {
      if (err?.status === 429) {
        setBudget((current) =>
          current ? { ...current, usedPercent: 100 } : current,
        );
        setAlert({ type: 'error', open: true, message: t('Your AI budget is used up') });
      } else {
        setAlert({
          type: 'error',
          open: true,
          message: t('The text could not be rewritten.'),
        });
      }
    } finally {
      setBusy(false);
    }
  }

  const tooltip = busy
    ? t('Rewriting…')
    : exhausted
    ? t('Your AI budget is used up')
    : hasSelection
      ? t('Rewrite the selected text with AI')
      : t('Improve the text with AI (select text to rewrite only that part)');

  const glow = hasSelection && !disabled;

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <Button
            className="grouped"
            disabled={disabled}
            // keep the editor's selection: focusing the button would blur it
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              minWidth: '37px',
              color: glow ? colors.info : undefined,
              animation: glow ? 'exoAiGlow 1.6s ease-in-out infinite' : 'none',
              '@keyframes exoAiGlow': {
                '0%': { boxShadow: `0 0 0 0 ${colors.info}00` },
                '50%': { boxShadow: `0 0 8px 2px ${colors.info}99` },
                '100%': { boxShadow: `0 0 0 0 ${colors.info}00` },
              },
            }}
          >
            <div className="w-4 h-4 flex justify-center items-center">
              {busy ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )}
            </div>
          </Button>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <Box className="px-4 py-1">
          <Typography variant="caption" className="opacity-60">
            {hasSelection
              ? t('Rewrite the selected text')
              : t('Rewrite the whole text')}
          </Typography>
        </Box>
        {STYLES.map((style) => (
          <MenuItem key={style.key} onClick={() => rewrite(style.key)}>
            {t(style.label)}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={(e) => setCustomAnchorEl(e.currentTarget)}>
          {t('Custom instruction…')}
        </MenuItem>
      </Menu>
      <Popover
        open={Boolean(customAnchorEl)}
        anchorEl={customAnchorEl}
        onClose={() => setCustomAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box className="flex flex-col gap-2 p-3" sx={{ width: 320 }}>
          <TextField
            autoFocus
            size="small"
            label={t('Custom instruction…')}
            placeholder={t('e.g. friendlier, as bullet points, in English')}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && custom.trim()) {
                e.preventDefault();
                rewrite('custom', custom.trim());
              }
            }}
            inputProps={{ maxLength: 500 }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!custom.trim()}
            onClick={() => rewrite('custom', custom.trim())}
          >
            {t('Rewrite')}
          </Button>
        </Box>
      </Popover>
      <ExoAlert
        alert={alert}
        open={Boolean(alert.open)}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </>
  );
};
